// src/pages/MasterData/DocumentSettingsPage.jsx
// =============================================================================
// Document Settings page
// =============================================================================
//
// PURPOSE
// -------
// This page maintains current default settings used by sales documents and
// future invoice PDF generation. It is intentionally not the invoice itself and
// it is also not an invoice snapshot.
//
// ARCHITECTURAL RULE
// ------------------
// Document Settings are editable Master Data defaults. When a sales document is
// posted, relevant values must be copied into document-level snapshots. Already
// posted invoice PDFs must render the snapshot stored on the document, not the
// latest defaults from this page.
//
// UX DECISIONS IN THIS VERSION
// ----------------------------
// 1. Payment method is selected from Master Data -> Payment Methods.
//    The user should not type a free-text payment method code here because the
//    code is later copied into payment snapshots and printed on invoices.
//
// 2. Place of issue has an explicit explanatory hint.
//    In Polish invoice practice this is usually the city/place where the
//    document is formally issued, for example "Warszawa" or "Kraków". It does
//    not have to be the full company address because that belongs to Company
//    Master Data / seller snapshot.
//
// 3. Number series uses an "Automatic by document type" option instead of a
//    cryptic "-".
//    Leaving the field empty is not an error. It means the backend will select
//    the active/default numbering series for the actual posted document type:
//    invoice, receipt or internal sale. This is the recommended mode because one
//    global Document Settings row should not accidentally force all document
//    types to use the invoice numbering pattern.
//
// 4. Choosing a concrete series is treated as an advanced/global override.
//    It is still supported because the backend field exists, but the UI explains
//    that it should be used only if the salon intentionally wants one selected
//    series to override automatic document-type selection for this settings
//    profile.

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import masterDataConfigService from "../../services/masterDataConfigService";
import { fetchCompanyMasterData } from "../../services/companyMasterDataService";
import "./MasterDataPage.css";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asString(value) {
  return String(value ?? "").trim();
}

function localName(row, language) {
  const isPolish = String(language || "").toLowerCase().startsWith("pl");
  return asString(isPolish ? row?.name_pl : row?.name_en) ||
    asString(row?.name_en) ||
    asString(row?.name_pl) ||
    asString(row?.code);
}

function documentTypeLabel(t, documentType) {
  const normalized = asString(documentType || "invoice").toLowerCase();
  return t(`masterData:documentTypes.${normalized}`, { defaultValue: normalized });
}

function Field({ label, hint, children, wide = false }) {
  return (
    <label className={`master-data-form-field${wide ? " master-data-form-field--wide" : ""}`}>
      <span>{label}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

export default function DocumentSettingsPage() {
  const { t, i18n } = useTranslation(["masterData", "common"]);
  const [form, setForm] = useState(null);
  const [banks, setBanks] = useState([]);
  const [series, setSeries] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [legalTexts, setLegalTexts] = useState([]);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setError(null);

        // Load all dependencies in one request group so the form can render
        // stable dropdowns. Payment Methods and Sales Number Series are Master
        // Data collections and therefore should be selected, not typed.
        const [settings, company, seriesRows, paymentMethodRows, legalTextRows] = await Promise.all([
          masterDataConfigService.getDocumentSettings(),
          fetchCompanyMasterData({ includeInactive: true }),
          masterDataConfigService.listSalesNumberSeries({ includeInactive: true }),
          masterDataConfigService.listPaymentMethods({ includeInactive: true }),
          masterDataConfigService.listLegalTexts({ includeInactive: true }),
        ]);

        if (!mounted) return;

        setForm(settings || {});
        setBanks(asArray(company?.bankAccounts || company?.bank_accounts));
        setSeries(asArray(seriesRows));
        setPaymentMethods(asArray(paymentMethodRows));
        setLegalTexts(asArray(legalTextRows));
      } catch (e) {
        if (mounted) setError(e.message);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const selectedSeries = useMemo(() => {
    const selectedId = asString(form?.default_sales_number_series_id);
    if (!selectedId) return null;
    return series.find((row) => asString(row?.id) === selectedId) || null;
  }, [form?.default_sales_number_series_id, series]);

  const selectedPaymentMethod = useMemo(() => {
    const selectedId = asString(form?.default_payment_method_id);
    if (!selectedId) return null;
    return paymentMethods.find((row) => asString(row?.id) === selectedId) || null;
  }, [form?.default_payment_method_id, paymentMethods]);

  const normalizedPaymentMethods = useMemo(() => {
    // Document Settings now stores only default_payment_method_id. The payment
    // method code stays in the Payment Methods dictionary and is resolved by the
    // backend when creating invoice snapshots. This avoids conflicting state
    // such as id=3 but code="cash".
    return [...paymentMethods].sort((a, b) => {
      const aOrder = Number(a?.sort_order || 0);
      const bOrder = Number(b?.sort_order || 0);
      if (aOrder !== bOrder) return aOrder - bOrder;
      return asString(a?.code).localeCompare(asString(b?.code));
    });
  }, [paymentMethods]);

  const normalizedSeries = useMemo(() => {
    return [...series].sort((a, b) => {
      const aType = asString(a?.document_type);
      const bType = asString(b?.document_type);
      if (aType !== bType) return aType.localeCompare(bType);
      const aOrder = Number(a?.sort_order || 0);
      const bOrder = Number(b?.sort_order || 0);
      if (aOrder !== bOrder) return aOrder - bOrder;
      return asString(a?.code).localeCompare(asString(b?.code));
    });
  }, [series]);


  const normalizedLegalTexts = useMemo(() => {
    return [...legalTexts].sort((a, b) => {
      const aArea = asString(a?.usage_area);
      const bArea = asString(b?.usage_area);
      if (aArea !== bArea) return aArea.localeCompare(bArea);
      const aOrder = Number(a?.sort_order || 0);
      const bOrder = Number(b?.sort_order || 0);
      if (aOrder !== bOrder) return aOrder - bOrder;
      return asString(a?.text_key).localeCompare(asString(b?.text_key));
    });
  }, [legalTexts]);

  function legalTextLabel(row) {
    const isPolish = String(i18n.language || "").toLowerCase().startsWith("pl");
    return asString(isPolish ? row?.title_pl : row?.title_en) ||
      asString(row?.title_en) ||
      asString(row?.title_pl) ||
      asString(row?.text_key);
  }

  const set = (key, value) => {
    setForm((old) => ({ ...(old || {}), [key]: value }));
  };

  const save = async () => {
    try {
      setSaved(false);
      setError(null);
      setForm(await masterDataConfigService.updateDocumentSettings(form));
      setSaved(true);
    } catch (e) {
      setError(e.message);
    }
  };

  if (!form) {
    return (
      <div className="master-data-page">
        <div className="master-data-page__inner master-data-page__inner--narrow">
          <section className="master-data-card">{t("common:loading")}</section>
        </div>
      </div>
    );
  }

  return (
    <div className="master-data-page">
      <div className="master-data-page__inner master-data-page__inner--narrow">
        <header className="master-data-header">
          <div>
            <p className="master-data-eyebrow">{t("masterData:common.backendConnected")}</p>
            <h1>{t("masterData:documentSettingsPage.title")}</h1>
            <p className="master-data-subtitle">{t("masterData:documentSettingsPage.subtitle")}</p>
          </div>
        </header>

        {error ? (
          <section className="master-data-warning-card">
            <strong>{t("masterData:messages.error")}</strong>
            <p>{error}</p>
          </section>
        ) : null}

        {saved ? <section className="master-data-success-card">{t("masterData:messages.saved")}</section> : null}

        <section className="master-data-info-grid master-data-info-grid--document-settings">
          <article className="master-data-info-card master-data-info-card--accent">
            <h2>{t("masterData:documentSettingsPage.placeOfIssueHelpTitle")}</h2>
            <p>{t("masterData:documentSettingsPage.placeOfIssueHelpText")}</p>
          </article>
          <article className="master-data-info-card">
            <h2>{t("masterData:documentSettingsPage.numberSeriesHelpTitle")}</h2>
            <p>{t("masterData:documentSettingsPage.numberSeriesHelpText")}</p>
          </article>
        </section>

        <section className="master-data-card">
          <div className="master-data-card__header">
            <div>
              <h2>{t("masterData:documentSettingsPage.formTitle")}</h2>
              <p>{t("masterData:documentSettingsPage.formIntro")}</p>
            </div>
          </div>

          <div className="master-data-form-grid">
            <Field label={t("masterData:fields.nameEn")}>
              <input value={form.name_en || ""} onChange={(e) => set("name_en", e.target.value)} />
            </Field>

            <Field label={t("masterData:fields.namePl")}>
              <input value={form.name_pl || ""} onChange={(e) => set("name_pl", e.target.value)} />
            </Field>

            <Field
              label={t("masterData:fields.placeOfIssue")}
              hint={t("masterData:documentSettingsPage.placeOfIssueHint")}
            >
              <input
                value={form.default_place_of_issue || ""}
                placeholder={t("masterData:documentSettingsPage.placeOfIssuePlaceholder")}
                onChange={(e) => set("default_place_of_issue", e.target.value)}
              />
            </Field>

            <Field label={t("masterData:fields.currency")}>
              <input
                value={form.default_currency || "PLN"}
                onChange={(e) => set("default_currency", e.target.value)}
              />
            </Field>

            <Field label={t("masterData:fields.language")}>
              <select value={form.default_language || "pl"} onChange={(e) => set("default_language", e.target.value)}>
                <option value="pl">PL</option>
                <option value="en">EN</option>
              </select>
            </Field>

            <Field label={t("masterData:fields.paymentTermDays")}>
              <input
                type="number"
                min="0"
                value={form.default_payment_term_days || 0}
                onChange={(e) => set("default_payment_term_days", e.target.value)}
              />
            </Field>

            <Field
              label={t("masterData:fields.paymentMethod")}
              hint={
                selectedPaymentMethod?.requires_bank_account
                  ? t("masterData:documentSettingsPage.paymentMethodRequiresBankHint")
                  : t("masterData:documentSettingsPage.paymentMethodHint")
              }
            >
              <select
                value={form.default_payment_method_id || ""}
                onChange={(e) => {
                  setForm((old) => ({
                    ...(old || {}),
                    default_payment_method_id: e.target.value || null,
                  }));
                }}
              >
                <option value="">{t("masterData:documentSettingsPage.noDefaultPaymentMethod")}</option>
                {normalizedPaymentMethods.map((method) => {
                  const code = asString(method?.code);
                  const name = localName(method, i18n.language);
                  const inactiveSuffix = method?.is_active === false
                    ? ` ${t("masterData:common.inactiveSuffix")}`
                    : "";
                  return (
                    <option key={method.id || code} value={method.id || ""}>
                      {code} — {name}{inactiveSuffix}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label={t("masterData:fields.bankAccount")} hint={t("masterData:documentSettingsPage.bankAccountHint")}>
              <select
                value={form.default_bank_account_id || ""}
                onChange={(e) => set("default_bank_account_id", e.target.value || null)}
              >
                <option value="">{t("masterData:documentSettingsPage.noDefaultBankAccount")}</option>
                {banks.map((bank) => (
                  <option key={bank.id} value={bank.id}>
                    {bank.account_label || bank.bank_name || bank.iban || bank.account_number || `#${bank.id}`}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label={t("masterData:fields.numberSeries")}
              hint={
                selectedSeries
                  ? t("masterData:documentSettingsPage.specificNumberSeriesHint")
                  : t("masterData:documentSettingsPage.automaticNumberSeriesHint")
              }
              wide
            >
              <select
                value={form.default_sales_number_series_id || ""}
                onChange={(e) => set("default_sales_number_series_id", e.target.value || null)}
              >
                <option value="">{t("masterData:documentSettingsPage.automaticNumberSeriesOption")}</option>
                {normalizedSeries.map((row) => {
                  const label = localName(row, i18n.language);
                  const inactiveSuffix = row?.is_active === false
                    ? ` ${t("masterData:common.inactiveSuffix")}`
                    : "";
                  return (
                    <option key={row.id} value={row.id}>
                      {documentTypeLabel(t, row.document_type)} · {row.code} — {row.pattern} — {label}{inactiveSuffix}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label={t("masterData:fields.invoiceFooterLegalText")} hint={t("masterData:documentSettingsPage.invoiceFooterLegalTextHint")}>
              <select
                value={form.default_invoice_footer_legal_text_id || ""}
                onChange={(e) => {
                  const selected = normalizedLegalTexts.find((row) => asString(row?.id) === e.target.value) || null;
                  setForm((old) => ({
                    ...(old || {}),
                    default_invoice_footer_legal_text_id: e.target.value || null,
                    default_invoice_footer_text_key: selected?.text_key || null,
                  }));
                }}
              >
                <option value="">{t("masterData:documentSettingsPage.noDefaultLegalText")}</option>
                {normalizedLegalTexts
                  .filter((row) => !row.usage_area || ["invoice_footer", "general_legal_note", "payment_instruction"].includes(row.usage_area))
                  .map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.usage_area} · {row.text_key} — {legalTextLabel(row)}
                    </option>
                  ))}
              </select>
            </Field>

            <Field label={t("masterData:fields.receiptFooterLegalText")} hint={t("masterData:documentSettingsPage.receiptFooterLegalTextHint")}>
              <select
                value={form.default_receipt_footer_legal_text_id || ""}
                onChange={(e) => set("default_receipt_footer_legal_text_id", e.target.value || null)}
              >
                <option value="">{t("masterData:documentSettingsPage.noDefaultLegalText")}</option>
                {normalizedLegalTexts
                  .filter((row) => !row.usage_area || ["receipt_footer", "general_legal_note"].includes(row.usage_area))
                  .map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.usage_area} · {row.text_key} — {legalTextLabel(row)}
                    </option>
                  ))}
              </select>
            </Field>

            <Field label={t("masterData:fields.paymentNoteLegalText")} hint={t("masterData:documentSettingsPage.paymentNoteLegalTextHint")}>
              <select
                value={form.default_payment_note_legal_text_id || ""}
                onChange={(e) => set("default_payment_note_legal_text_id", e.target.value || null)}
              >
                <option value="">{t("masterData:documentSettingsPage.noDefaultLegalText")}</option>
                {normalizedLegalTexts
                  .filter((row) => !row.usage_area || ["payment_instruction", "general_legal_note"].includes(row.usage_area))
                  .map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.usage_area} · {row.text_key} — {legalTextLabel(row)}
                    </option>
                  ))}
              </select>
            </Field>

            <Field label={t("masterData:fields.invoiceLayout")}>
              <input
                value={form.default_invoice_layout_version || "classic_v1"}
                onChange={(e) => set("default_invoice_layout_version", e.target.value)}
              />
            </Field>

            <Field label={t("masterData:fields.invoicePdfOrientation")} hint={t("masterData:documentSettingsPage.invoicePdfOrientationHint")}>
              <select
                value={form.invoice_pdf_orientation || "portrait"}
                onChange={(e) => set("invoice_pdf_orientation", e.target.value)}
              >
                <option value="portrait">{t("masterData:documentSettingsPage.orientationPortrait")}</option>
                <option value="landscape">{t("masterData:documentSettingsPage.orientationLandscape")}</option>
              </select>
            </Field>

            {["show_company_logo_on_invoice", "show_payment_block_on_invoice", "show_bank_account_on_invoice", "show_discount_columns_on_invoice", "show_skonto_on_invoice", "show_legal_footer_on_invoice"].map((key) => (
              <label key={key} className="master-data-check-field master-data-form-field">
                <input
                  type="checkbox"
                  checked={form[key] !== false}
                  onChange={(e) => set(key, e.target.checked)}
                />
                <span>
                  <strong>{t(`masterData:fields.${key}`)}</strong>
                  <small>{t(`masterData:documentSettingsPage.printFlags.${key}`)}</small>
                </span>
              </label>
            ))}
          </div>

          <section className="master-data-warning-card master-data-warning-card--compact master-data-document-settings-note">
            <strong>{t("masterData:documentSettingsPage.numberSeriesDecisionTitle")}</strong>
            <p>{t("masterData:documentSettingsPage.numberSeriesDecisionText")}</p>
          </section>

          <div className="master-data-actions-row">
            <button className="master-data-primary-btn" type="button" onClick={save}>
              {t("masterData:actions.save")}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
